// Administration → Moteurs: the « Connexion des comptes » section first (web/ui/admin/accounts.js:
// log the Claude and ChatGPT accounts in, the Gemini and Kie.ai keys), then one card per engine
// (Claude Code, Codex, Images OpenAI, Gemini, Kie.ai, Simulation) with its state, version and error, the install / login /
// check steps from docs/DEPLOY-MACMINI.md §4 (commands with a copy button), a VÉRIFIER À NOUVEAU
// button (POST /api/engines/refresh) and a banner when the mock engine is forced (OFFICE_ENGINE=mock).
import { h, button, badge, statusDot, icon } from '../../dom.js';
import { t } from '../../i18n/fr.js';
import { relativeTime, formatDateTime } from '../../format.js';
import { ENGINE_KEYS, engineState } from './model.js';
import { sectionHead, loadingState, metaItem, commandLine, setBusy } from './common.js';
import { createAccountsSection } from './accounts.js';
import './strings.js';

/**
 * `engines.status()` reports `pending: true` until its first check ends (`--version` 15 s at most,
 * then the account 20 s at most): polled that long, the live updates (SSE `engines`) doing the rest.
 */
const PENDING_POLL_MS = 2000;
const PENDING_POLLS = 20;
const REFRESH_TIMEOUT_MS = 60_000;

/**
 * Install / login / check steps per engine (docs/DEPLOY-MACMINI.md §4). Each step:
 * `{title, commands?: string[], hint?: string}` — commands are shown verbatim with a copy button.
 * @param {string} key engine key
 * @returns {Array<{title: string, commands?: string[], hint?: string}>}
 */
export function engineHelp(key) {
  switch (key) {
    case 'claude':
      return [
        { title: t('adm.eng.step.install'), commands: ['curl -fsSL https://claude.ai/install.sh | bash', 'claude --version'] },
        { title: t('adm.eng.step.login'), commands: ['claude'], hint: t('adm.eng.hint.claude.login') },
        { title: t('adm.eng.step.check'), commands: ['claude -p "Réponds seulement : OK"', 'claude mcp list'], hint: t('adm.eng.hint.claude.check') },
      ];
    case 'codex':
      return [
        { title: t('adm.eng.step.install'), commands: ['npm install -g @openai/codex'] },
        { title: t('adm.eng.step.login'), commands: ['codex login'], hint: t('adm.eng.hint.codex.login') },
        { title: t('adm.eng.step.check'), commands: ['codex login status'] },
      ];
    case 'openai-image':
      return [
        { title: t('adm.eng.step.install'), hint: t('adm.eng.hint.openai.key'), commands: ['cd ~/maeck-office', 'touch config/.env && chmod 600 config/.env', 'open -e config/.env'] },
        { title: t('adm.eng.step.enable'), hint: t('adm.eng.hint.openai.enable'), commands: ['deploy/macmini/install.sh'] },
      ];
    case 'gemini':
      return [
        { title: t('adm.eng.step.install'), hint: t('adm.eng.hint.gemini.key'), commands: ['cd ~/maeck-office', 'touch config/.env && chmod 600 config/.env', 'open -e config/.env'] },
        { title: t('adm.eng.step.enable'), hint: t('adm.eng.hint.gemini.enable'), commands: ['deploy/macmini/install.sh'] },
        { title: t('adm.eng.step.check'), hint: t('adm.eng.hint.gemini.cli'), commands: ['npm install -g @google/gemini-cli', 'gemini --version'] },
      ];
    case 'kie':
      return [
        { title: t('adm.eng.step.install'), hint: t('adm.eng.hint.kie.key') },
        { title: t('adm.eng.step.check'), hint: t('adm.eng.hint.kie.public') },
      ];
    case 'mock':
      return [{ title: t('adm.eng.step.check'), hint: t('adm.eng.hint.mock') }];
    default:
      return [];
  }
}

/**
 * Normalises GET /api/engines (or the bootstrap `engines`) into a plain object.
 * @param {unknown} res
 * @returns {object}
 */
export function normalizeEngines(res) {
  return res && typeof res === 'object' && !Array.isArray(res) ? res : {};
}

/**
 * Creates the engines tab.
 * @param {object} ctx app context
 * @returns {{el: HTMLElement, reload(): Promise<void>, destroy(): void}}
 */
export function createEnginesView(ctx) {
  let status = null;
  let unavailable = false;
  let failed = null;
  let destroyed = false;
  let pollTimer = null;
  let polls = 0;

  const refreshBtn = button(t('adm.eng.refresh'), { variant: 'primary', icon: 'refresh', onClick: () => refresh() });
  const head = sectionHead({ title: t('adm.eng.title'), intro: t('adm.eng.intro'), actions: [refreshBtn] });
  const notes = h('div', { class: 'adm-eng-notes' });
  const body = h('div', { class: 'adm-body' }, loadingState());
  // After a login, a logout or a new Gemini key the server re-checks the engines in the
  // background, and GET /api/engines would still answer the old status: ask for the check
  // instead (POST /api/engines/refresh joins the one already running).
  // A hosted instance (account logins on) has the CLIs preinstalled: the Mac install steps of
  // each card would mislead, the accounts section above replaces them.
  let hosted = false;
  const accounts = createAccountsSection(ctx, {
    onChange: () => refresh({ quiet: true }),
    onData: (d) => {
      const next = !!(d?.loginsEnabled && !d.mock);
      if (next === hosted) return;
      hosted = next;
      if (status) render();
    },
  });
  const el = h('section', { class: 'adm-view adm-engines' }, head, accounts.el, notes, body);

  function useStatus(next) {
    status = normalizeEngines(next);
    // Keep the top bar's engine badges in step with what the admin just checked.
    ctx.store.set({ engines: status });
  }

  async function load() {
    try {
      const res = await ctx.api.get('/api/engines');
      if (destroyed) return;
      useStatus(res);
      unavailable = false;
      failed = null;
    } catch (err) {
      if (destroyed) return;
      // Fall back on the bootstrap copy of the status.
      status = normalizeEngines(ctx.store.get().engines);
      unavailable = err.status === 404;
      failed = unavailable ? null : err;
    }
    render();
    schedulePendingPoll();
  }

  function schedulePendingPoll() {
    clearTimeout(pollTimer);
    pollTimer = null;
    const pending = ENGINE_KEYS.some((k) => status?.[k]?.pending);
    if (destroyed || !pending || polls >= PENDING_POLLS) return;
    polls += 1;
    pollTimer = setTimeout(load, PENDING_POLL_MS);
  }

  /**
   * Checks the engines again (VÉRIFIER À NOUVEAU). `quiet` (after an account change): no busy
   * button, no second read of the accounts, and a failure falls back on GET /api/engines.
   */
  async function refresh({ quiet = false } = {}) {
    if (!quiet) {
      setBusy(refreshBtn, true);
      accounts.reload({ force: true });
    }
    try {
      const res = await ctx.api.post('/api/engines/refresh', {}, { timeoutMs: REFRESH_TIMEOUT_MS });
      if (destroyed) return;
      useStatus(res);
      failed = null;
      polls = 0;
      render();
      schedulePendingPoll();
    } catch (err) {
      if (destroyed) return;
      if (quiet) {
        polls = 0;
        load();
      } else ctx.toast.error(err);
    } finally {
      if (!quiet && !destroyed) setBusy(refreshBtn, false);
    }
  }

  function render() {
    const st = status || {};
    const forced = !!(st.mock?.forced || ctx.store.get().office?.mock);
    const noteEls = [];
    if (forced) noteEls.push(h('p', { class: 'adm-alert is-amber', role: 'note' }, icon('alert', { size: 16 }), h('span', {}, t('adm.eng.mockForced'))));
    if (hosted) noteEls.push(h('p', { class: 'adm-alert is-info', role: 'note' }, icon('info', { size: 16 }), h('span', {}, t('adm.eng.hosted'))));
    if (unavailable) noteEls.push(h('p', { class: 'adm-alert is-info' }, icon('info', { size: 16 }), h('span', {}, t('adm.eng.unavailable'))));
    if (failed) noteEls.push(h('p', { class: 'adm-alert is-danger', role: 'alert' }, icon('alert', { size: 16 }), h('span', {}, failed.message || t('common.error'))));
    if (st.checkedAt) {
      noteEls.push(h('p', { class: 'adm-eng-checked muted', title: formatDateTime(st.checkedAt) },
        icon('clock', { size: 14 }), h('span', {}, t('adm.eng.checkedAt', { when: relativeTime(st.checkedAt) }))));
    }
    notes.replaceChildren(...noteEls);
    refreshBtn.disabled = unavailable;
    body.replaceChildren(h('div', { class: 'adm-eng-grid' }, ENGINE_KEYS.map((key) => engineCard(key, st[key], forced))));
  }

  function engineCard(key, st, forced) {
    const state = engineState(key, key === 'mock' ? { ok: true, ...st, forced } : st);
    const help = hosted ? [] : engineHelp(key);
    const error = st && !st.ok && st.error ? String(st.error) : '';
    const meta = [];
    if (key !== 'mock' && st?.version) meta.push(metaItem(t('adm.eng.version'), String(st.version), { mono: true }));
    if (key === 'gemini' && st?.mode) meta.push(metaItem(t('adm.eng.mode'), t(`adm.eng.mode.${st.mode === 'cli' ? 'cli' : 'api'}`)));
    if (key === 'gemini' && st?.model) meta.push(metaItem(t('adm.eng.model'), String(st.model), { mono: true }));
    if (key === 'kie' && st?.model && st.model !== st.version) meta.push(metaItem(t('adm.eng.model'), String(st.model), { mono: true }));
    return h('article', { class: ['adm-eng', `is-${state.key}`], dataset: { engine: key } },
      h('header', { class: 'adm-eng-head' },
        h('div', { class: 'adm-eng-titles' },
          h('h4', { class: 'adm-eng-name' }, t(`adm.eng.name.${key}`)),
          h('p', { class: 'adm-eng-desc' }, t(`adm.eng.desc.${key}`))),
        h('span', { class: 'adm-eng-state' }, statusDot(state.dot), badge(state.label, state.tone))),
      key === 'mock'
        ? h('p', { class: 'adm-eng-mock' }, forced ? t('adm.eng.mockOn') : t('adm.eng.mockOff'))
        : null,
      meta.length ? h('div', { class: 'adm-eng-meta' }, meta) : null,
      error ? h('p', { class: ['adm-alert', state.key === 'unchecked' ? 'is-amber' : 'is-danger', 'adm-eng-error'] }, icon('alert', { size: 16 }),
        h('span', {}, h('strong', {}, `${t('adm.eng.error')} : `), error)) : null,
      help.length ? h('details', { class: 'adm-eng-help', open: (state.key === 'error' || state.key === 'disconnected') && key === 'claude' },
        h('summary', { class: 'adm-eng-help-summary' }, t('adm.eng.howto')),
        h('ol', { class: 'adm-eng-steps' }, help.map((step) => h('li', { class: 'adm-eng-step' },
          h('span', { class: 'adm-eng-step-title mono-label' }, step.title),
          step.hint ? h('p', { class: 'adm-eng-step-hint' }, step.hint) : null,
          (step.commands || []).map((cmd) => commandLine(cmd, ctx))))),
        h('p', { class: 'adm-eng-doc muted' }, t('adm.eng.hint.doc'))) : null);
  }

  load();
  // A check that ends (start, the 15-minute recheck, a login elsewhere) reaches every page through
  // the store (main.js reloads it on the SSE event `engines`): the cards follow.
  const offEngines = ctx.store.on?.('engines', (next) => {
    if (destroyed || !next || typeof next !== 'object' || next === status) return;
    status = normalizeEngines(next);
    render();
    schedulePendingPoll();
  });
  return {
    el,
    reload() {
      accounts.reload();
      polls = 0;
      return load();
    },
    destroy() {
      destroyed = true;
      offEngines?.();
      clearTimeout(pollTimer);
      accounts.destroy();
    },
  };
}
